import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { CloudUpload, X, AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { getQueued, retryFailed, flushQueue, removeQueued, blobOf, assignQueuedProject } from "@/lib/offlineQueue";
import { api } from "@/lib/api";
import { useModalBack } from "@/lib/modalBack";
import { toast } from "sonner";

// Global upload-status pill (Sprint Item 4): visible whenever the offline
// queue isn't empty — "3 waiting to upload", live "12 of 20 uploaded" while
// flushing, red when anything failed. Tap → queue sheet with manual retry.
export default function UploadQueuePill() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justDone, setJustDone] = useState(false); // F: "All photos uploaded ✓" flash
  const [wtBuilding, setWtBuilding] = useState(false); // P0-4: background walkthrough build
  const [wtReady, setWtReady] = useState(false);
  const [savingCount, setSavingCount] = useState(0); // STOP-SHIP: in-memory rescue saves in flight
  const hadWorkRef = useRef(false);
  useModalBack(open, () => setOpen(false));

  // P0-4: "Building walkthrough…" → "Walkthrough ready ✓" while the report
  // assembles in the background (Done closed the camera instantly).
  useEffect(() => {
    const b = () => { setWtBuilding(true); setWtReady(false); };
    const r = () => { setWtBuilding(false); setWtReady(true); setTimeout(() => setWtReady(false), 4000); };
    window.addEventListener("bf:wt-building", b);
    window.addEventListener("bf:walkthrough-ready", r);
    return () => { window.removeEventListener("bf:wt-building", b); window.removeEventListener("bf:walkthrough-ready", r); };
  }, []);

  const refresh = useCallback(() => {
    getQueued().then((its) => {
      setItems(its);
      const work = its.some((i) => i.status === "pending" || i.status === "uploading" || i.status === "failed");
      if (work) { hadWorkRef.current = true; setJustDone(false); }
      else if (hadWorkRef.current) {
        hadWorkRef.current = false;
        setJustDone(true);
        setTimeout(() => setJustDone(false), 3000);
      }
    });
  }, []);
  useEffect(() => {
    refresh();
    const on = () => refresh();
    // Round 18 A: a photo that failed 5 uploads tells the user once.
    const onDead = () => toast.error(t("queue_dead_toast"));
    const onSaving = (e) => setSavingCount(e.detail?.count || 0);
    const onSaveFailed = () => toast.error(t("queue_save_failed"));
    window.addEventListener("bf:queue-changed", on);
    window.addEventListener("online", on);
    window.addEventListener("bf:queue-dead", onDead);
    window.addEventListener("bf:queue-saving", onSaving);
    window.addEventListener("bf:queue-save-failed", onSaveFailed);
    const iv = setInterval(refresh, 5000); // keeps "N of M uploaded" live on slow networks
    return () => {
      window.removeEventListener("bf:queue-changed", on);
      window.removeEventListener("online", on);
      window.removeEventListener("bf:queue-dead", onDead);
      window.removeEventListener("bf:queue-saving", onSaving);
      window.removeEventListener("bf:queue-save-failed", onSaveFailed);
      clearInterval(iv);
    };
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  // A1: the pill counts DISTINCT photos (legacy dupes collapse by client_id).
  const distinct = (arr) => {
    const seen = new Set();
    return arr.filter((i) => { const k = i.client_id || `q${i.qid}`; if (seen.has(k)) return false; seen.add(k); return true; });
  };
  const waiting = distinct(items.filter((i) => i.status === "pending" || i.status === "uploading"));
  const failed = distinct(items.filter((i) => i.status === "failed"));
  // STOP-SHIP (B): expired login — one-tap sign-in prompt, photos wait safely.
  const authItems = distinct(items.filter((i) => i.status === "auth"));
  const needsProject = distinct(items.filter((i) => i.status === "needs_project"));
  const orphaned = distinct(items.filter((i) => i.status === "orphaned"));
  const jobless = [...needsProject, ...orphaned];
  const [projects, setProjects] = useState([]);
  const [dismissedSig, setDismissedSig] = useState("");
  // Banner dismissal persisted across pages/reloads (James): the signature is
  // the current set of problem qids; a NEW orphan/failure changes it and the
  // banner re-shows. All dismiss state/hooks live BEFORE the early returns
  // below (auth/saving/empty branches) so hook order never changes.
  const netIssue = (typeof navigator !== "undefined" && !navigator.onLine)
    || waiting.some((i) => i.error === "network");
  const codeIssue = !netIssue && (failed.length > 0 || waiting.some((i) => i.error && i.error !== "network"));
  const problemSig = jobless.length
    ? `j:${jobless.map((i) => i.qid).join(".")}`
    : (failed.length || codeIssue) ? `f:${failed.map((i) => i.qid).join(".")}` : "";
  useEffect(() => {
    let saved = "";
    try { saved = localStorage.getItem("bf.queue.dismissed") || ""; } catch { /* non-browser */ }
    setDismissedSig(problemSig && saved === problemSig ? problemSig : "");
  }, [problemSig]);
  const dismissBanner = () => {
    try { localStorage.setItem("bf.queue.dismissed", problemSig); } catch { /* non-browser */ }
    setDismissedSig(problemSig);
  };
  useEffect(() => {
    // Orphaned photos (job deleted) need the picker too — gating the fetch on
    // needsProject only left orphaned rows with an EMPTY dropdown: the banner
    // could never be resolved (the "2555 Arbuckle" stuck-banner bug).
    if (open && jobless.length && !projects.length) {
      api.get("/projects").then(({ data }) => setProjects((Array.isArray(data) ? data : data.items || []).slice(0, 100))).catch(() => {});
    }
  }, [open, jobless.length, projects.length]);
  if (authItems.length > 0) {
    return (
      <button onClick={() => { window.location.href = "/login"; }} data-testid="queue-auth-pill"
        className="fixed bottom-24 left-1/2 z-[80] inline-flex min-h-[40px] -translate-x-1/2 items-center gap-2 rounded-full bg-red-600 px-4 py-1 text-xs font-bold text-white shadow-lg active:scale-95">
        <AlertTriangle className="h-3.5 w-3.5" /> {t("queue_sign_in", { count: authItems.length })}
      </button>
    );
  }
  // STOP-SHIP: a photo still waiting on its IndexedDB save shows as
  // "Saving N photos…" — the camera already closed; the photo is in memory.
  if (savingCount > 0) {
    return (
      <div data-testid="queue-saving-pill" role="status"
        className="fixed bottom-24 left-1/2 z-[80] inline-flex min-h-[40px] -translate-x-1/2 items-center gap-2 rounded-full bg-amber-500 px-4 py-1 text-xs font-bold text-white shadow-lg">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("queue_saving", { count: savingCount })}
      </div>
    );
  }
  // Jobless photos (no job, or their job was deleted) fall THROUGH to the
  // main pill as an amber banner — the same button opens the sheet with the
  // job picker (the early-return banner used to open nothing: the sheet only
  // rendered in the main branch).
  // Hide once nothing is actionable — uploaded confirmations linger in IDB for
  // 1h for the Photos-page panel, but the pill must not say "0 waiting".
  // F: when the queue drains to zero, confirm once — "All photos uploaded ✓".
  if (!waiting.length && !failed.length && !jobless.length) {
    if (wtBuilding) {
      return (
        <div data-testid="wt-building-pill" role="status"
          className="fixed bottom-24 left-1/2 z-[80] inline-flex min-h-[40px] -translate-x-1/2 items-center gap-2 rounded-full bg-amber-500 px-4 py-1 text-xs font-bold text-white shadow-lg">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("wt_building")}
        </div>
      );
    }
    if (wtReady) {
      return (
        <div data-testid="wt-ready-pill" role="status"
          className="fixed bottom-24 left-1/2 z-[80] inline-flex min-h-[40px] -translate-x-1/2 items-center gap-2 rounded-full bg-green-600 px-4 py-1 text-xs font-bold text-white shadow-lg">
          <CheckCircle2 className="h-3.5 w-3.5" /> {t("wt_ready_done")}
        </div>
      );
    }
    if (!justDone) return null;
    return (
      <div data-testid="upload-queue-done" role="status"
        className="fixed bottom-24 left-1/2 z-[80] inline-flex min-h-[40px] -translate-x-1/2 items-center gap-2 rounded-full bg-green-600 px-4 py-1 text-xs font-bold text-white shadow-lg">
        <CheckCircle2 className="h-3.5 w-3.5" /> {t("queue_pill_done")}
      </div>
    );
  }
  const doneRecent = distinct(items.filter((i) => i.status === "uploaded"));
  const flushing = waiting.some((i) => i.status === "uploading");
  const joblessOnly = !waiting.length && !failed.length && jobless.length;
  const label = joblessOnly
    ? (needsProject.length
        ? t("queue_pill_needs_job", { count: needsProject.length })
        : t("queue_job_deleted", { count: orphaned.length }))
    : failed.length
    ? t("queue_pill_failed", { count: failed.length })
    : flushing
      ? t("queue_pill_progress", { done: doneRecent.length, total: doneRecent.length + waiting.length })
      : t("queue_pill_waiting", { count: waiting.length });

  // Never show raw internal IDs / walkthrough handles as a row label.
  const rowTitle = (it) => {
    const f = it.filename || "";
    if (!f || /^wt-[0-9a-f-]/i.test(f) || /^[0-9a-f-]{20,}$/i.test(f) || /\.(json|tmp)$/i.test(f)) {
      return t(`queue_st_${it.status}`);
    }
    return f;
  };

  const retry = async () => {
    setBusy(true);
    try { await retryFailed(); await flushQueue(api); } finally { setBusy(false); refresh(); }
  };

  // Honest subtext: blame the signal ONLY after a real network error —
  // anything else is an upload problem the user can retry (and we report it).
  // (netIssue/codeIssue are computed above with the dismiss logic — the hooks
  // must live before the early returns.)

  // Fast Photos (E): after 10 failed tries the photo is likely stuck for
  // good — offer a manual "Save to phone" so the proof shot is never lost.
  const saveToPhone = async (it) => {
    const blob = blobOf(it);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = it.filename || "photo.jpg";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    await removeQueued(it.qid);
    toast.success(t("queue_saved_phone"));
    refresh();
  };

  // One short health sentence (user-approved) whenever uploads are struggling.
  const unhealthy = failed.length > 0 || waiting.some((i) => (i.attempts || 0) > 0);

  // Hard rule (user): a problem banner must ALWAYS be dismissible.
  if (problemSig && dismissedSig === problemSig) return null;

  return (
    <>
      <div className="fixed bottom-[max(0px,calc(env(safe-area-inset-bottom,0px)+96px))] left-1/2 z-[60] flex -translate-x-1/2 items-center gap-1.5">
      <button onClick={() => { if (failed.length || codeIssue) retry(); setOpen(true); }}
        data-testid={joblessOnly ? "queue-needs-job-banner" : "upload-queue-pill"}
        className={`inline-flex min-h-[40px] items-center gap-2 rounded-full px-4 py-1 text-xs font-bold text-white shadow-lg active:scale-95 ${joblessOnly ? "bg-amber-500" : failed.length || codeIssue ? "bg-red-600" : "bg-slate-900/90"}`}>
        {joblessOnly || failed.length || codeIssue ? <AlertTriangle className="h-3.5 w-3.5" />
          : flushing ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <CloudUpload className="h-3.5 w-3.5" />}
        <span className="flex flex-col items-start leading-tight">
          <span>{label}</span>
          {codeIssue
            ? <span className="text-[10px] font-semibold opacity-90" data-testid="upload-queue-health">{t("queue_pill_problem")}</span>
            : unhealthy && netIssue
              ? <span className="text-[10px] font-semibold opacity-80" data-testid="upload-queue-health">{t("queue_pill_health")}</span>
              : null}
        </span>
      </button>
      {problemSig && (
        <span role="button" tabIndex={0} aria-label={t("queue_dismiss")}
          data-testid="queue-banner-dismiss"
          onClick={(e) => { e.stopPropagation(); dismissBanner(); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); dismissBanner(); } }}
          className="flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-full bg-slate-900/80 text-white shadow-lg active:scale-95">
          <X className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
      </div>
      {open && createPortal(
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50" onClick={() => setOpen(false)}
          data-testid="upload-queue-sheet">
          <div className="flex max-h-[70vh] w-full max-w-md flex-col rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))]"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-extrabold text-slate-900">{t("queue_sheet_title")}</h3>
              <button onClick={() => setOpen(false)} data-testid="upload-queue-close" aria-label={t("action_cancel")}
                className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="mt-3 flex-1 space-y-1.5 overflow-y-auto" data-testid="upload-queue-list">
              {distinct(items).map((it) => (
                <li key={it.qid} className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-3 py-2"
                  data-testid={`queue-item-${it.qid}`}>
                  {it.status === "uploaded" ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                    : it.status === "failed" ? <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
                    : it.status === "uploading" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" />
                    : <CloudUpload className="h-4 w-4 shrink-0 text-slate-400" />}
                  {blobOf(it) && (
                    <img src={URL.createObjectURL(blobOf(it))} alt="" data-testid={`queue-thumb-${it.qid}`}
                      className="h-9 w-9 shrink-0 rounded-lg object-cover border border-slate-200" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-bold text-slate-800">{rowTitle(it)}</span>
                    <span className="block text-[11px] text-slate-500">
                      {t(`queue_st_${it.status}`)}{it.error ? ` · ${it.error}` : ""}
                      {it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
                    </span>
                    {(it.status === "needs_project" || it.status === "orphaned") && (
                      <select className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700"
                        data-testid={`queue-jobpick-${it.qid}`} value=""
                        onChange={(e) => e.target.value && assignQueuedProject(it.qid, e.target.value).then(() => flushQueue(api)).then(refresh)}>
                        <option value="">{t(it.status === "orphaned" ? "queue_move_job" : "queue_needs_job")}</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    )}
                  </span>
                  {((it.fails || 0) >= 10 || it.status === "orphaned" || it.status === "needs_project") && blobOf(it) && (
                    <button onClick={() => saveToPhone(it)} data-testid={`queue-save-${it.qid}`}
                      className="shrink-0 rounded-full bg-brand/10 px-3 py-1.5 text-[11px] font-bold text-brand">
                      {t("queue_save_phone")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {failed.length > 0 && (
              <button onClick={retry} disabled={busy} data-testid="upload-queue-retry"
                className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-brand px-5 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("queue_retry")}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
